import { useMemo, useState, useEffect, useRef } from 'react';
import '../../accounting/accounting.css';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame
import './departmentDetail.css';
import './viewLock.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AiCombo } from '../../../components/AiCombo';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useNavActions } from '../../../hooks/useNavActions';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { api, runWithAuditReason } from '../../../api/client';
import { useDepartments, useSaveDepartment, useDeleteDepartment, departmentParentOptions, errMessage, type Department } from './systemMasters';
import { DepartmentEmployeesPanel } from './DepartmentEmployeesPanel';
import { DepartmentChildPanel } from './DepartmentChildPanel';

interface Props { onHome?: () => void; }

/** Settings ▸ System Management ▸ Department — CRUD over dbo.Department (Name + Sort). */
export function DepartmentPage({ onHome }: Props) {
  const notify = useNotify();
  // Module Access Control: hide (never grey) the actions the signed-in role does not hold.
  const perm = useNavActions('sm-department');
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useDepartments();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveDepartment();
  const del = useDeleteDepartment();

  const [search, setSearch] = useState('');

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  const [editing, setEditing] = useState<Department | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [sort, setSort] = useState('');
  const [parentId, setParentId] = useState('');
  const [err, setErr] = useState('');
  const [stagedEmpIds, setStagedEmpIds] = useState<Set<string>>(new Set());        // employees linked to a NEW (unsaved) department, committed on save
  const [stagedChildIds, setStagedChildIds] = useState<Set<string>>(new Set());   // child departments linked to a NEW (unsaved) department, committed on save

  // View → Edit (owner request 2026-10-12): a click on a row opens the record READ-ONLY with an Edit
  // button; Edit turns the boxes and the Save buttons on. New always opens editable.
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const viewOnly = mode === 'view' && !!editing?.id;
  const openNew = () => {
    setMode('edit');
    const nextSort = rows.reduce((m, r) => Math.max(m, r.sort ?? 0), 0) + 1;   // next Sort = MAX+1 (LIMS pre-fills it)
    setEditing(null); setName(''); setSort(String(nextSort)); setParentId(''); setErr(''); setStagedEmpIds(new Set()); setStagedChildIds(new Set());
  };
  const openView = (r: Department) => { openEdit(r); setMode('view'); };
  const openEdit = (r: Department) => { setMode(perm.write ? 'edit' : 'view'); setEditing(r); setName(r.name ?? ''); setSort(r.sort != null ? String(r.sort) : ''); setParentId(r.parentId ?? ''); setErr(''); setStagedEmpIds(new Set()); setStagedChildIds(new Set()); };
  const [dtab, setDtab] = useState<'employees' | 'children'>('employees');   // detail's lower tab
  const close = () => setEditing(undefined);
  // Cancel / breadcrumb / record arrows: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(close); };

  // Toolbar Edit: the ONE ticked row opens in the Detail View, editable (owner request 2026-10-15 — the in-row
  // editor was removed; the row icon opens the same Detail View).
  const editSelected = () => {
    if (selectedIds.size !== 1) return;
    const [id] = [...selectedIds];
    const r = rows.find((x) => x.id === id);
    if (r) openEdit(r);
  };

  // LIMS New/Edit footer offers three save variants — Save (persist, stay open),
  // Save and Close (persist + back to list), Save and New (persist + fresh form).
  const submit = async (mode: 'save' | 'close' | 'new' = 'close') => {
    if (!name.trim()) { setErr('Department must not be empty'); await alert({ title: 'Validation', message: 'Department must not be empty', tone: 'error' }); return; }
    setErr('');
    const sortNum = sort.trim() === '' ? null : Number(sort);
    let savedId: string | null = editing?.id ?? null;
    const wasNew = !editing?.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('Department', 'edit');
      if (r === null) return;
      reason = r;
    }
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        const newId = await save.mutateAsync({ id: editing?.id, name: name.trim(), sort: sortNum, parentId: parentId || null });
        savedId = newId;
        // New department: commit the employees staged on the Employees tab.
        if (wasNew && newId && stagedEmpIds.size > 0) {
          for (const empId of stagedEmpIds) await api.put(`/api/v1/system/departments/${newId}/employees/${empId}`, {});
        }
        // New department: re-parent the child departments staged on the Child Department tab.
        if (wasNew && newId && stagedChildIds.size > 0) {
          for (const childId of stagedChildIds) {
            const c = rows.find((d) => d.id === childId);
            if (c) await save.mutateAsync({ id: c.id, name: c.name ?? '', sort: c.sort, parentId: newId });
          }
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (mode === 'close') { close(); return; }
        if (mode === 'new') { openNew(); return; }
        // 'save' — keep the form open; a brand-new record now becomes an edit.
        if (wasNew && savedId) {
          setStagedEmpIds(new Set());
          setStagedChildIds(new Set());
          setEditing({ id: savedId, name: name.trim(), sort: sortNum, parentId: parentId || null, parent: rows.find((d) => d.id === parentId)?.name ?? null });
        }
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one department to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}?` })) return;
    const reason = await gateReason('Department', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  // Detail shortcuts through the shared hotkey layer (standing rule 14).
  useHotkeys(editing === undefined ? [] : viewOnly ? [
    ...(perm.write ? [
      { combo: 'alt+e', label: 'Edit', group: 'Department', order: 1, run: () => setMode('edit') },
      { combo: 'f2', label: 'Edit', group: 'Department', order: 2, run: () => setMode('edit') },
    ] : []),
    { combo: 'esc', label: 'Close', group: 'Department', order: 3, allowInInput: false, run: close },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Department', order: 1, run: () => void submit('save') },
    ...(perm.create ? [{ combo: 'mod+shift+s', label: 'Save & New', group: 'Department', order: 2, run: () => void submit('new') }] : []),
    { combo: 'mod+enter', label: 'Save & Close', group: 'Department', order: 3, run: () => void submit('close') },
    { combo: 'esc', label: 'Cancel', group: 'Department', order: 4, allowInInput: false, run: leave },
  ]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => (r.name ?? '').toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const columns: AppColumn<Department>[] = [
    // Opens the EDITABLE Detail View (owner request 2026-10-15; was the in-row editor) — hidden for a role without Write.
    ...(perm.write ? [{
      field: 'detailedit', header: '', noExport: true, sortable: false, filterable: false, width: 44, align: 'center' as const, text: () => '',
      render: (r: Department) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label={`Edit ${r.name ?? 'department'}`}
          onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-edit" /></button>
      ),
    }] : []),
    {
      // LIMS splits Department / Parent / Sort as roughly equal thirds that fill
      // the grid width (Sort right-aligned), so match that proportion here.
      field: 'name', header: 'Department', width: '34%', minWidth: 200, text: (r) => r.name ?? '',
      compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
      render: (r) => <span>{r.name ?? ''}</span>,
    },
    {
      field: 'parent', header: 'Parent', width: '33%', minWidth: 180, sortable: false, text: (r) => r.parent ?? '',
      render: (r) => <span>{r.parent ?? ''}</span>,
    },
    {
      field: 'sort', header: 'Sort', width: '33%', minWidth: 120, align: 'right', type: 'number', sortable: false, value: (r) => r.sort ?? null, text: (r) => r.sort != null ? String(r.sort) : '',
      render: (r) => <span>{r.sort != null ? String(r.sort) : ''}</span>,
    },
  ];

  // ── Full-page detail (New / row double-click) — replaces the list while open (standing rule 12;
  //    owner request 2026-10-12: legacy opens the detail view, not a popup). Same frame as the
  //    City, Division and Country forms. ──
  if (editing !== undefined) {
    const recordName = editing ? (editing.name ?? '').trim() || 'Department' : 'New Department';
    const curIdx = editing ? rows.findIndex((r) => r.id === editing.id) : -1;
    const prevRow = curIdx > 0 ? rows[curIdx - 1] : null;
    const nextRow = curIdx >= 0 && curIdx < rows.length - 1 ? rows[curIdx + 1] : null;
    const actions = (bottom?: boolean) => viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        {perm.write && <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>}
        <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => void submit('save')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'Save'}</button>
        <button className="sd-abtn" onClick={() => void submit('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
        {perm.create && <button className="sd-abtn" onClick={() => void submit('new')} disabled={save.isPending} title={`Save and New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save and New</button>}
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
      </div>
    );
    return (
      <div className="bl-page mlv-host">
        {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
            there are unsaved changes. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
        </div>
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>Department – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                  <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                  <a onClick={leave}>Department</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button className="sd-na" title="Previous record" disabled={!prevRow || save.isPending} onClick={() => prevRow && void guardedExit(() => (viewOnly ? openView : openEdit)(prevRow))}>‹</button>
                <button className="sd-na next" title="Next record" disabled={!nextRow || save.isPending} onClick={() => nextRow && void guardedExit(() => (viewOnly ? openView : openEdit)(nextRow))}>›</button>
              </div>
            </div>

            {actions()}

            <div className="sd-body dep-detail">
              <fieldset className="sm-viewlock dep-fields" disabled={viewOnly}>
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">Department<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(''); }} />
                  {err && <div className="acc-err">{err}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">Parent</label>
                <div className="acc-fh-control">
                  {/* Parent lookup: dropdown + erase (clear) icon — LIMS Department.Parent self-FK. */}
                  <AiCombo value={parentId} options={departmentParentOptions(rows)}
                           onChange={(v) => setParentId(v ?? '')} />
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">Sort</label>
                <div className="acc-fh-control">
                  <input className="acc-input" type="number" value={sort} placeholder="(auto)" onChange={(e) => setSort(e.target.value)} />
                </div>
              </div>
              </fieldset>
              {/* Employees | Child Department tabs, as the legacy Department detail (Child Department
                  restored 2026-10-12, owner report "Missing other Tab"). */}
              <div className="acc-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={dtab === 'employees'} className={`acc-tab${dtab === 'employees' ? ' active' : ''}`} onClick={() => setDtab('employees')}><i className="ti ti-users" /> Employees</button>
                <button type="button" role="tab" aria-selected={dtab === 'children'} className={`acc-tab${dtab === 'children' ? ' active' : ''}`} onClick={() => setDtab('children')}><i className="ti ti-sitemap" /> Child Department</button>
              </div>
              <div className={viewOnly ? 'sm-viewlock-panel' : undefined}>
                {dtab === 'employees'
                  ? <DepartmentEmployeesPanel departmentId={editing?.id} staged={stagedEmpIds} onStagedChange={setStagedEmpIds} />
                  : <DepartmentChildPanel departmentId={editing?.id} staged={stagedChildIds} onStagedChange={setStagedChildIds} />}
              </div>
            </div>

            {actions(true)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Department</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id); if (r) openView(r);
            }} />
          <FormTitle pageId="sm-department" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Department" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        {!gridMax && (
        <AppToolbar
          onNew={perm.create ? openNew : undefined}
          onEdit={perm.write ? editSelected : undefined} editDisabled={selectedIds.size !== 1}
          onDelete={perm.delete ? () => void removeSelected() : undefined} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="Department" headers={['Department', 'Parent', 'Sort']} rows={visible.map((r) => [r.name ?? '', r.parent ?? '', r.sort != null ? String(r.sort) : ''])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="Department" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<Department> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => openView(r)}
            initialSort={{ key: 'name', dir: 'asc' }} loading={isFetching} emptyText="No departments found." hideHeaderTools />
        </div>
      </div>

    </div>
  );
}
